import { constants } from '../const';
import { Observable } from 'rxjs';
import { IndividualAdditionalRole, IndividualIdDocument, PrivacyConsent, RoleTypes, SignerRoleTypes } from './application-service.domain';
import {
  ApplicationIndividual,
  ApplicationIndividualAdditionalRoleWrite,
  ApplicationIndividualWrite,
} from './application-parties.domain';
import { ApiResponse, PaginablePayloadApiResponse, PayloadApiResponse } from './types.domain';
import { ParsedAddress } from '../utils/address';
import { IdentityVerificationResult } from './rekognition.domain';
import { MessageBoxType } from './component/message-box-component.domain';
import { KybReconciliationStatus, KybResultStatus } from './kyb-key-people.domain';

export interface ParsedDigitalIdResponse {
  firstName: string;
  middleName: string;
  lastName: string;
  country: string;
  state: string;
  postcode: string;
  suburb: string;
  rawAddress: string;
  streetName: string;
  streetNumber: string;
  streetType: string;
  unitNumber: string;
  birthdate: string /* YYYY-MM-DD */;
}

export interface UpdateIndividualData {
  title?: string | null;
  firstName?: string | null;
  middleName?: string | null;
  lastName?: string | null;
  state?: string | null;
  postcode?: string | null;
  suburb?: string | null;
  mobileNumber?: string | null;
  email?: string | null;
  guarantor?: boolean | null;
  rawAddress?: string | null;
  streetName?: string | null;
  streetNumber?: string | null;
  streetType?: string | null;
  unitNumber?: string | null;
  birthdate?: string | null;
  deleted?: boolean | null;
  /**
   * Whether the individual signs for the borrower entity.
   *
   * Was the SignerRole string (AZ-1475), which also carried guarantor status - so this patch stated
   * that twice, here and in `guarantor` above, and the server resolved the disagreement by letting
   * the string win. `guarantor` is now the only thing that says so, and a caller changing one
   * without the other means it.
   */
  isSigner?: boolean;
  additionalRoles?: ApplicationIndividualAdditionalRoleWrite[];
  contact?: string | null;
  country?: string | null;
  hasAlias?: boolean | null;
  aliasNames?: string | null;
  countryOfCitizenship?: string | null;
  identificationType?: string | null;
  identificationNumber?: string | null;
  propertyOwner?: boolean | null;
  propertyValue?: number | null;
  mortgageBalance?: number | null;
  // Address of the owned property - separate from the home address fields above.
  propertyOwnedRawAddress?: string | null;
  propertyOwnedStreetName?: string | null;
  propertyOwnedStreetNumber?: string | null;
  propertyOwnedStreetType?: string | null;
  propertyOwnedUnitNumber?: string | null;
  propertyOwnedSuburb?: string | null;
  propertyOwnedState?: string | null;
  propertyOwnedPostcode?: string | null;
}

export interface Alert {
  type:
  | 'success'
  | 'info'
  | 'warning'
  | 'danger'
  | 'primary'
  | 'secondary'
  | 'light'
  | 'dark';
  message: string;
}

export interface KycStatusMessage {
  type: MessageBoxType;
  message: string;
  showDetails: boolean;
  showPrint: boolean;
  detailsTooltip?: string;
  printIcon?: string;
}

export interface DigitalIdResultReceived {
  status: KycStatus;
  creationDate: Date;
  lastUpdate: Date;
  digitalIdResponse: DigitalIdResponse;
  code?: string | null;
  error?: string | null;
  isNameOrDobChanged?: boolean | null;
  failureEmailSentAt?: Date | null;
  verificationEmailSentAt?: Date | null;
  frankieOneEntityId?: string | null;
  history?: KycHistoryRecord[];
}

export interface DigitalIdResultError {
  status: 'RECEIVED_ERROR';
  creationDate: Date;
  lastUpdate: Date;
  error: any;
  code?: string | null;
  isNameOrDobChanged?: boolean | null;
  failureEmailSentAt?: Date | null;
  verificationEmailSentAt?: Date | null;
  frankieOneEntityId?: string | null;
  history?: KycHistoryRecord[];
}

export interface DigitalIdResponse {
  verification_status: 'in_progress' | 'completed' | 'failed' | 'pending_review';
  verification_session_token?: string; // when 'completed' verification_session_token can be undefined
  data_source_events?: (
    | 'credit_header_check_failed'
    | 'name_and_address_matched'
    | 'name_and_dob_matched'
    | 'kyc_basic_callback_result_success'
    | 'frankieone_kyc_verification_passed'
    | 'frankieone_kyc_verification_failed'
  )[];
  watchlist?: {
    check_performed: boolean;
    check_performed_date: string;
    found: boolean;
    found_sources: {
      name: string;
      category: 'pep' | 'sanctions';
    }[];
    sources_category: 'pep' | 'sanctions' | 'pep_and_sanctions';
  };
  transaction_id?: string;
  frankieOneCallback?: FrankieOneCallbackPayload; // FrankieOne callback data when received
  verifiedByUserId?: number;
  verifiedByName?: string;
  overallRiskLevel?: string;
  // Numeric FrankieOne risk score. Absent from the KYC callback, so it is fetched from the workflow
  // execution result. Kept when staff later override the KYC status; stays undefined when KYC was
  // never run through FrankieOne, and for records created before the score was captured.
  riskScore?: number;
  verificationMethod?: string;
  // NEW: Structured error with user-friendly messages
  verificationError?: KycVerificationError;
  detailsMismatchError?: KycVerificationError;
}

export type DigitalIdResult = DigitalIdResultError | DigitalIdResultReceived;

export type KycStatus =
  | 'WAITING'
  | 'SMS_SENT'
  | 'RECEIVED_COMPLETE'
  | 'RECEIVED_INCOMPLETE'
  | 'RECEIVED_ERROR'
  | 'none'
  | 'RECEIVED_FAILED'
  | 'RECEIVED_PENDING_REVIEW'; // FrankieOne callback received - awaiting internal staff review

export interface DigitalIdResultReceived {
  status: KycStatus;
  creationDate: Date;
  lastUpdate: Date;
  digitalIdResponse: DigitalIdResponse;
  code? : string | null;
  error?: string | null;
  isNameOrDobChanged?: boolean | null;
  failureEmailSentAt?: Date | null;
  verificationEmailSentAt?: Date | null;
  frankieOneEntityId?: string | null;
  history?: KycHistoryRecord[];
}

export interface DigitalIdResultError {
  status: 'RECEIVED_ERROR';
  creationDate: Date;
  lastUpdate: Date;
  error: any;
  code?: string | null;
  isNameOrDobChanged?: boolean | null;
  failureEmailSentAt?: Date | null;
  verificationEmailSentAt?: Date | null;
  frankieOneEntityId?: string | null;
  history?: KycHistoryRecord[];
}

export interface ThirdPartyEntity {
  address: string;
  abn: string;
  acn: string;
  organisationName: string;
}

/**
 * The body POST /digitalId/addIndividual takes: an individual in the API write shape, plus the two
 * things that describe the add itself rather than the person.
 *
 * Was declared in the legacy field names (AZ-1475), which made this the last endpoint speaking them
 * and the reason the legacy-to-rows adapter could not be retired. `Role` and `SignerRole` are gone
 * with it: the write shape carries the role flags and isSigner directly, so the strings no longer
 * have to be parsed server-side, and a role holding two flags - a director who is also a
 * shareholder - survives, where the single string could only hold one.
 */
export type AddIndividual = ApplicationIndividualWrite & {
  /**
   * Owning entity for a trustee/partner added post-submission from the KYC/AML tab. Undefined
   * assigns the role to the primary borrower (the existing default for applicants/guarantors).
   */
  parentCommercialEntityId?: number;
  /**
   * ApplicationIndividual.source to tag the row with, for a caller that adds someone deliberately
   * incomplete (see PORTAL_GUARANTOR_INDIVIDUAL_SOURCE). Undefined leaves it untagged, as every
   * other add does.
   */
  source?: string;
};

/**
 * An individual as the digital-ID endpoint returns it: the API read shape plus the four things
 * getApplicationIndividuals grafts on afterwards, each from its own query.
 *
 * Was `extends Individual` (AZ-1475), and was declared twice in this file - the two declarations
 * merged silently, so the second one's fields were reaching consumers of the first.
 *
 * The grafted fields live here rather than on ApplicationIndividual because none of them is
 * reachable from the parties snapshot that projection reads; declaring them there would leave them
 * permanently undefined on every other producer. See docs/az-1475-party-model.md 10.3 / 11.7.
 */
export interface IndividualWithResult extends ApplicationIndividual {
  result: DigitalIdResult;
  identityVerificationResult?: IdentityVerificationResult;
  privacyConsent?: PrivacyConsent[];
  /** From the individual-document table, keyed by guid - not from the parties snapshot. */
  idDocuments?: IndividualIdDocument[];
}

// ====== payloads

export interface DigitalIdClientInfoPayload {
  clientId: string;
  kycClientId: string;
  scriptUrl: string;
}

export interface DigitalIdAuthenticationPayload {
  accessToken: string;
  tokenType: string;
  expiresIn: string;
  scope: string;
  individualInfo: {
    transaction_id: string;
    sub: string;
    birthdate: string /* YYYY-MM-DD */;
    address: {
      formatted: string;
      street_address: string;
      locality: string;
      region: string;
      postal_code: string;
      country: string;
    };
    digitalid: {
      object: string;
      address_verified: boolean;
    };
    iss: string;
    given_name: string;
    middle_name: string;
    client_id: string;
    aud: string;
    updated_at: number;
    name: string;
    exp: number;
    family_name: string;
    iat: number;
  };
}

export type UpdateApplicationIndividualInfoPayload = IndividualWithResult;

export interface UpdateApplicationIndividualDigitalIdMappingPayload {
  entriesUpdated: number;
}
export interface UpdateApplicationIndividualDigitalIdMappingInput {
  verificationStatus: 'completed' | 'in_progress';
  dataSourceEvents: string[];
  verificationSessionToken?: string;
  transactionId: string;
}

export interface GetApplicationindividualsPayload {
  applicationId: string;
  brokerAppId: string;
  status: string;
  individuals: IndividualWithResult[];
}

export type VerifyOneApplicationIndividualPayload = IndividualWithResult;

export type UpdateIndividualPayload = ApplicationIndividual;

export type SetupApplicationIndividualMapping = IndividualWithResult[];

export type DigitalIdUpdateIndividualKycStatusFn = (applicationId: number, individualId: string, kycStatus: KycStatus, note: string) => Observable<ApiResponse>;

export interface SyncIndividualKycFromFrankieOnePayload {
  updated: boolean;
}

/** Returns the full response so the caller can toast `message` and refresh only when `payload.updated`. */
export type DigitalIdSyncIndividualKycFromFrankieOneFn = (
  applicationId: number,
  individualId: string,
) => Observable<PayloadApiResponse<SyncIndividualKycFromFrankieOnePayload>>;

export interface ApplicationVerificationIndividual {
  individualId: string;
  individual: ApplicationIndividual;
  status: KycStatus;
  creationDate: Date;
  lastUpdate: Date;
  digitalIdResponse: DigitalIdResponse | null;
  code: string;
}

export interface ApplicationVerificationPayloadResult {
  applicationId: number;
  brokerAppId: string;
  companyName?: string;
  individuals: ApplicationVerificationIndividual[];
}

// ApplicationVerificationPayload is now just the result directly
// Errors are thrown as ClientError and handled by the error middleware
export type ApplicationVerificationPayload = ApplicationVerificationPayloadResult;

export interface DoKycVerificationData {
  given_name: string;
  middle_name: string;
  family_name: string;
  dob: string;
  address: ParsedAddress;
  consent: boolean;
}

export interface DoKycVerficationDataInput extends DoKycVerificationData {
  address: {
    unit_number?: string;
    street_number: string;
    street_name: string;
    street_type: string | null;
    locality: string; // suburb
    region: keyof typeof constants.stateList; // state
    postal_code: string;
    country: 'AUS';
  };
}

export interface UserDigitalIdMapping {
  id: number;
  userId: number;
  code: string;
  creationDate: string;
}

export interface DigitalIdVerifyDetail {
  data: DigitalIdResult & { verificationUrl: string }
}

export interface PersonalInfoDetail {
  data: {
    State?: string;
    Expiry_Date: string;
    DOB: string;
    Firstname: string;
    Middlename: string;
    Surname: string;
    License_No: string;
    Card_No: string;
    Licence_Class?: string;
    Postcode?: string;
    Street?: string;
    Suburb?: string;
    googleParsedAddress: string;
    addressObject?: {
      street_number: string;
      street_name: string;
      street_type: string;
      locality: string;
      region: string;
      postal_code: string;
      country: string;
    };
  };
}

export type OverdraftUserVerificationData = OverdraftUserVerificationByLicence | OverdraftUserVerificationByIndividual;

export interface OverdraftUserVerificationByLicence {
  type: 'licence',
  licence: {
    data: string,
    type: string
  }
}

export interface OverdraftUserVerificationByIndividual {
  type: 'individual',
  individual: {
    firstName: string,
    middleName: string,
    lastName: string,
    dob: string,  // YYYY-MM-DD
    driverLicenceNumber?: string,
    driverCardNumber?: string,
    driverCardExpiry?: string,
    address: {
      unitNumber?: string,  // optional
      streetNumber: string,
      streetName: string,
      streetType: string,
      locality: string,  // suburb
      region: string,  // state
      postCode: string,
    },
    postalAddress?: {  // postal address is optional (if do not exists will be the same as address)
      unitNumber?: string,  // optional
      streetNumber: string,
      streetName: string,
      streetType: string,
      locality: string,  // suburb
      region: string,  // state
      postCode: string,
    }
  }
}

export interface EditKycStatusDialogData {
  kycStatus: KycStatus | undefined;
}

export interface EditKycStatusDialogResult {
  kycStatus: EditKycStatusValue;
  note: string;
  readyForSubmission: boolean;
}

export type EditKycStatusValue = KycStatus | null;

export interface OnCompleteMessageSuccess {
  code: string;
  transaction_id: string;
}
export interface OnCompleteMessageError {
  error: string;
  error_description: string;
  transaction_id: string;
}
export type OnCompleteMessage =
  | OnCompleteMessageSuccess
  | OnCompleteMessageError;

// ====== FrankieOne KYC Types ======

// FrankieOne Issue Categories
export type FrankieOneIssueCategory =
  | 'AML' | 'BAD_DATA' | 'BIOMETRICS' | 'BLOCKLISTED' | 'DECEASED'
  | 'DEVICE' | 'DOCUMENT' | 'DUPLICATE' | 'EXPIRY' | 'FRAUD'
  | 'IDV' | 'INFORMATIONAL' | 'KYC' | 'SYSTEM' | 'VERIFY'
  | 'VISA' | 'WATCHLIST' | 'ACTIVITY';

// FrankieOne Issue Severity Levels
export type FrankieOneIssueSeverity =
  | 'INFO' | 'REVIEW' | 'BLOCK' | 'WARNING' | 'CRITICAL' | 'ERROR';

// Individual Issue from FrankieOne workflow
export interface FrankieOneWorkflowIssue {
  issueId: string;
  category: FrankieOneIssueCategory;
  issue: string;  // e.g., 'NOT_FOUND', 'BAD_DATA_ID', 'LIVENESS_DETECTION'
  severity: FrankieOneIssueSeverity;
  isCleared?: boolean;
  createdAt: string;
  workflowExecutionId?: string;
  mappedDescription?: string;
}

// Structured error for UI display
export interface KycVerificationError {
  userMessage: string;
  suggestions: string[];
}

// KYC History / Audit Log Types
export type KycEventType = 'SMS_SENT' | 'CALLBACK_RECEIVED' | 'STATUS_CHANGE' | 'MANUAL_UPDATE';

export interface KycHistoryRecord {
  id: number;
  applicationId: number;
  individualId: string;
  eventType: KycEventType;
  status: KycStatus;
  error?: string;
  digitalIdResponse?: DigitalIdResponse;
  eventData?: any;
  createdAt: Date;
  createdBy?: string; // User's full name (e.g., "John Smith") or user ID if name not found
  note?: string; // Admin note explaining the status change
}

export interface DigitalIdResultWithHistory extends DigitalIdResultReceived {
  history?: KycHistoryRecord[];
}

export interface FrankieOneEkycRequest {
  customerRef: string;
  consent: boolean;
  phoneNumber: string;
  phoneCode: string;
  recipientName: string;
  oneSDKFlowId: string;
  sendSMS: boolean;
  callBackURL: string;
  systemId: string;
  callBackSubKey: string;
  /** Existing FrankieOne entity to continue verification on. Omitted to create a new entity. */
  entityId?: string;
}

export interface FrankieOneEkycResponse {
  entityId: string;
  requestId: string;
  status: string;
  url: string;
  urlExpiry: string;
}

export interface FrankieOneCallbackPayload {
  version: string;
  entityCustomerReference: string;
  entityId: string;
  function: 'RESULTS_RETRIEVED' | 'TOKEN_REQUESTED' | 'WorkflowComplete' | 'EntityStatusChanged' | 'EntityRiskChanged';
  functionResult: 'SUCCESS' | 'FAIL' | string;
  message: string;
  notificationType: string;
  requestId: string;
  workflowExecutionId?: string;
  workflowName?: string;
  serviceName?: string;
  entityType: string;
  overallRiskLevel?: 'LOW' | 'MEDIUM' | 'HIGH' | 'UNACCEPTABLE';
  overallStatus?: 'PASS' | 'FAIL' | 'REVIEW' | string;
  channel?: string;
  verifiedPersonDetails?: {
    firstName: string;
    displayName?: string;
    middleName?: string;
    middleNme?: string;
    surname: string;
    addresses?: any[];
    dateOfBirth: string;
    gender?: string;
    phoneNumber?: string;
  };
  // NEW: Issues array from FrankieOne workflow results
  issues?: FrankieOneWorkflowIssue[];
}

export type FrankieOneCustomerRefType = 'IND' | 'USR' | 'APP';

export interface FrankieOneKycVerificationData {
  customerRef: string;
  recipientName: string;
  phoneNumber: string;
  phoneCode: string;
  consent: boolean;
  /** Existing FrankieOne entity to continue verification on. Omitted to create a new entity. */
  entityId?: string;
}

export interface FrankieOneKycResult {
  entityId: string;
  requestId: string;
  verificationUrl: string;
  urlExpiry: string;
}

/** Risk assessment block on a FrankieOne workflow execution result. */
export interface FrankieOneRiskAssessment {
  riskAssessmentId?: string;
  serviceProfileId?: string;
  riskLevel?: string;
  riskScore?: number;
  workflowRiskLevel?: string;
  workflowRiskScore?: number;
}

/** Per-step running risk on a FrankieOne workflow execution. */
export interface FrankieOneWorkflowStepRisk {
  contributedScore?: number;
  level?: string;
  overallScore?: number;
}

export interface FrankieOneWorkflowStepResult {
  stepName?: string;
  endedAt?: string;
  updatedAt?: string;
  risk?: FrankieOneWorkflowStepRisk;
}

/**
 * Result of a FrankieOne individual workflow execution. Only the fields we consume are modelled;
 * the payload also carries the full entity record and per-step results.
 */
export interface FrankieOneIndividualExecutionResult {
  requestId?: string;
  workflowResult?: {
    entityId?: string;
    workflowExecutionId?: string;
    workflowExecutionState?: string;
    status?: string;
    riskAssessment?: FrankieOneRiskAssessment;
    workflowStepResults?: FrankieOneWorkflowStepResult[];
  };
}

/** Issue on a GET /v2/individuals workflow summary. Fields the portal does not render are omitted. */
export interface FrankieOneWorkflowSummaryIssue {
  issueId?: string;
  category?: string;
  issue?: string;
  severity?: string;
  isCleared?: boolean;
  createdAt?: string;
  workflowExecutionId?: string;
}

/** Workflow summary embedded on a service profile from GET /v2/individuals/{entityId}. */
export interface FrankieOneWorkflowExecutionSummary {
  workflowName?: string;
  workflowExecutionId?: string;
  workflowExecutionState?: string;
  status?: string;
  startedAt?: string;
  endedAt?: string;
  issues?: FrankieOneWorkflowSummaryIssue[];
  riskAssessment?: FrankieOneRiskAssessment;
}

export interface FrankieOneIndividualServiceProfile {
  serviceName?: string;
  workflowSummaries?: FrankieOneWorkflowExecutionSummary[];
}

export interface FrankieOneIndividualEntityIndividual {
  name?: {
    givenName?: string;
    middleName?: string;
    familyName?: string;
    displayName?: string;
  };
  dateOfBirth?: {
    year?: string;
    month?: string;
    day?: string;
    normalized?: string;
  };
}

/** Response of FrankieOne GET /v2/individuals/{entityId}. Only the fields the KYC sync reads. */
export interface FrankieOneIndividualEntityResponse {
  requestId?: string;
  individual?: FrankieOneIndividualEntityIndividual;
  serviceProfiles?: FrankieOneIndividualServiceProfile[];
}

/** Latest workflow summary plus the service profile it belongs to. */
export interface FrankieOneWorkflowSummaryWithService extends FrankieOneWorkflowExecutionSummary {
  serviceName?: string;
}

export interface InitiateFrankieOnePayload {
  verificationUrl: string;
  status: string;
  code: string;
  entityId?: string;
  requestId?: string;
}

// --- FrankieOne KYB (organization) types ---

export interface FrankieOneKybWebhookPayload {
  version: string;
  entityId: string;
  entityType?: string;
  function: string;
  functionResult: 'SUCCESS' | 'FAILURE' | string;
  message?: string;
  notificationType?: string;
  requestId: string;
  workflowExecutionId?: string;
  workflowName?: string;
  serviceName?: string;
  entityCustomerReference?: string;
  overallStatus?: string;
  overallRiskLevel?: string;
  overallRiskStatus?: string;
  channel?: string;
}

export interface FrankieOneKybInitiationResult {
  applicationId: number;
  applicationCommercialEntityId: number;
  entityId: string;
  requestId: string;
  workflowExecutionId: string;
  serviceName: string;
  workflowName: string;
  entityCustomerReference: string;
}

export interface FrankieOneKybWorkflowResultSummary {
  entityId: string;
  requestId?: string;
  workflowExecutionId: string;
  workflowName?: string;
  serviceName?: string;
  workflowExecutionState?: string;
  status?: string;
  overallRiskLevel?: string;
  issues?: FrankieOneWorkflowIssue[];
  applicationKybResultId?: number;
  stakeholderCount?: number;
}

export type FrankieOneKybWorkflowReference = {
  applicationId: number;
  applicationCommercialEntityId?: number;
  entityId: string;
  requestId: string;
  workflowExecutionId: string;
  workflowName: string;
  serviceName: string;
  entityCustomerReference: string;
  functionResult: string;
};

/** Payload for delayed KYB initiation retries after store-application. */
export type FrankieOneKybInitiateJobPayload = {
  applicationId: number;
  applicationKybRunId: number;
};

/** Payload for delayed KYB detailed-result processing retries after WorkflowComplete. */
export type FrankieOneKybResultJobPayload = {
  applicationId: number;
  applicationKybRunId: number;
};

/**
 * Custom risk attributes patched onto a FrankieOne organization before its workflow is re-evaluated.
 * Recorded on the ApplicationKYBReevaluation row as the attribute set that produced that re-score.
 */
export type FrankieOneRiskAttributes = {
  anzsicCode: string;
  channel: string;
  product: string;
};

/**
 * Payload for delayed risk-attribute patch + workflow re-evaluation retries.
 *
 * Carries the re-evaluation row so a retry updates the attempt that failed rather than opening a new
 * one — a retry is the same request again, whereas a fresh trigger is a new request.
 */
export type FrankieOneRiskReevaluationJobPayload = {
  applicationId: number;
  applicationKybRunId: number;
  applicationKybReevaluationId: number;
  /**
   * Re-evaluate without patching custom attributes, for a re-score triggered by an individual's risk
   * change rather than an attribute edit.
   */
  evaluateOnly?: boolean;
};

/** Minimal Gregorian date fragment from FrankieOne organization/stakeholder payloads. */
export interface FrankieOneKybDateFragment {
  day?: string;
  month?: string;
  year?: string;
  normalized?: string;
  unstructured?: string;
  type?: string;
}

export interface FrankieOneKybCodeDescription {
  code?: string;
  description?: string;
  sourceId?: string;
}

export interface FrankieOneKybRegistrationDetail {
  isCurrent?: boolean;
  registeredCountry?: string;
  registrationNumber?: string;
  registrationNumberType?: string;
  registrationNumberDescription?: string;
  registry?: string;
  registryDescription?: string;
  registeredDate?: FrankieOneKybDateFragment;
  registeredName?: { name?: string; sourceId?: string };
  registrationStatus?: {
    normalized?: FrankieOneKybCodeDescription;
    unstructured?: FrankieOneKybCodeDescription;
    sourceId?: string;
  };
  status?: {
    normalized?: FrankieOneKybCodeDescription;
    unstructured?: FrankieOneKybCodeDescription;
    sourceId?: string;
  };
  type?: FrankieOneKybCodeDescription;
  class?: FrankieOneKybCodeDescription;
  subClass?: FrankieOneKybCodeDescription;
  sourceId?: string;
}

export interface FrankieOneKybAddress {
  addressId?: string;
  type?: string;
  unitNumber?: string;
  streetNumber?: string;
  streetName?: string;
  streetType?: string;
  locality?: string;
  subdivision?: string;
  postalCode?: string;
  country?: string;
  /** Provider-formatted single-line address; present more often than the parsed street fields. */
  longForm?: string;
  unstructuredLongForm?: string;
  validFrom?: FrankieOneKybDateFragment;
  validTo?: FrankieOneKybDateFragment;
  sourceId?: string;
}

export interface FrankieOneKybLinkedIndividual {
  entityId?: string;
  entityType?: string;
  name?: {
    displayName?: string;
    givenName?: string;
    middleName?: string;
    familyName?: string;
  };
  dateOfBirth?: FrankieOneKybDateFragment;
  addresses?: FrankieOneKybAddress[];
}

export interface FrankieOneKybOfficial {
  entityId?: string;
  entityType?: string;
  role?: FrankieOneKybCodeDescription;
  appointmentDate?: FrankieOneKybDateFragment;
  status?: {
    unstructured?: FrankieOneKybCodeDescription;
    sourceId?: string;
  };
  sourceId?: string;
}

export interface FrankieOneKybShareholder {
  entityId?: string;
  entityType?: string;
  totalShares?: number;
  shareholdings?: Array<{
    class?: string;
    shares?: number;
    sourceId?: string;
  }>;
}

export interface FrankieOneKybUltimateBeneficialOwner {
  entityId?: string;
  entityType?: string;
  percentageOwned?: {
    beneficially?: number;
    nonBeneficially?: number;
    total?: number;
  };
  sourceId?: string;
}

export interface FrankieOneKybOrganization {
  entityId?: string;
  entityType?: string;
  details?: {
    name?: { name?: string; sourceId?: string };
    status?: {
      normalized?: FrankieOneKybCodeDescription;
      unstructured?: FrankieOneKybCodeDescription;
      sourceId?: string;
    };
    type?: FrankieOneKybCodeDescription;
    class?: FrankieOneKybCodeDescription;
    subClass?: FrankieOneKybCodeDescription;
    jurisdiction?: {
      country?: string;
      registry?: string;
      registryDescription?: string;
    };
    registrationDetails?: FrankieOneKybRegistrationDetail[];
  };
  addresses?: FrankieOneKybAddress[];
  /** Keyed by the `sourceId` referenced from addresses and other registry-sourced fields. */
  informationSources?: Record<string, { source?: string }>;
  linkedIndividuals?: Record<string, FrankieOneKybLinkedIndividual>;
  officials?: FrankieOneKybOfficial[];
  shareholders?: FrankieOneKybShareholder[];
  shareCapital?: {
    totalShareCount?: number;
  };
  ultimateBeneficialOwners?: FrankieOneKybUltimateBeneficialOwner[];
  otherOwners?: FrankieOneKybUltimateBeneficialOwner[];
  jurisdictionalInformation?: {
    registryInformation?: {
      gst?: Array<{ validFrom?: FrankieOneKybDateFragment }>;
      sourceId?: string;
    };
  };
}

export interface FrankieOneKybAmlStepSummary {
  totalHits?: number;
  totalTruePositives?: number;
  totalFalsePositives?: number;
  totalUnknown?: number;
  totalUnresolved?: number;
  numUnresolvedPEP?: number;
  numUnresolvedSanction?: number;
  numUnresolvedWatchlist?: number;
  numUnresolvedAdverseMedia?: number;
}

export interface FrankieOneKybWorkflowStepResult {
  stepName?: string;
  result?: string;
  processResults?: Array<{
    class?: string;
    entityId?: string;
    result?: string;
    state?: string;
    stepName?: string;
    systemStatus?: string;
    supplementaryData?: {
      organization?: FrankieOneKybOrganization;
    };
  }>;
  summary?: FrankieOneKybAmlStepSummary & {
    attachmentId?: string;
    documentId?: string;
    reportType?: string;
    isAllAssociatedEntitiesChecked?: boolean;
    uncheckedEntityIds?: string[];
    ownership?: { isComplete?: boolean };
    stepName?: string;
  };
  supplementaryData?: {
    organization?: FrankieOneKybOrganization;
  };
}

export interface FrankieOneKybWorkflowResult {
  createdAt?: string;
  startedAt?: string;
  endedAt?: string;
  updatedAt?: string;
  entityId?: string;
  entityType?: string;
  issues?: unknown[];
  requestId?: string;
  result?: string;
  status?: string;
  workflowExecutionId?: string;
  workflowExecutionState?: string;
  workflowId?: string;
  workflowName?: string;
  serviceProfileId?: string;
  riskAssessment?: {
    riskAssessmentId?: string;
    serviceProfileId?: string;
    riskLevel?: string;
    riskScore?: number;
    workflowRiskLevel?: string;
    workflowRiskScore?: number;
    workflowExecutionId?: string;
    workflowId?: string;
  };
  workflowStepResults?: FrankieOneKybWorkflowStepResult[];
  steps?: {
    failed?: string[];
    incomplete?: string[];
    notApplicable?: string[];
    order?: string[];
    passed?: string[];
  };
}

/** Full execution payload returned by FrankieOne KYB result GET. */
export interface FrankieOneKybExecutionResult {
  organization?: FrankieOneKybOrganization;
  requestId?: string;
  workflowResult?: FrankieOneKybWorkflowResult;
}

/** Curated per-run KYB result row persisted locally. */
export interface ApplicationKybResultRow {
  applicationKybRunId: number;
  applicationId: number;
  applicationCommercialEntityId: number;
  callbackFunctionResult?: string | null;
  callbackMessage?: string | null;
  workflowExecutionState?: string | null;
  workflowStatus?: string | null;
  workflowResult?: string | null;
  riskAssessmentId?: string | null;
  serviceProfileId?: string | null;
  riskLevel?: string | null;
  riskScore?: number | null;
  workflowRiskLevel?: string | null;
  workflowRiskScore?: number | null;
  registeredName?: string | null;
  abn?: string | null;
  acn?: string | null;
  organizationStatusCode?: string | null;
  organizationStatusDescription?: string | null;
  organizationTypeCode?: string | null;
  organizationTypeDescription?: string | null;
  organizationClassCode?: string | null;
  organizationClassDescription?: string | null;
  organizationSubClassCode?: string | null;
  organizationSubClassDescription?: string | null;
  registry?: string | null;
  registeredCountry?: string | null;
  jurisdictionCountry?: string | null;
  registeredDate?: string | null;
  gstRegisteredFrom?: string | null;
  isOwnershipComplete?: boolean | null;
  isAllAssociatedEntitiesChecked?: boolean | null;
  uncheckedEntityCount?: number | null;
  amlResult?: string | null;
  amlState?: string | null;
  amlTotalHits?: number | null;
  amlTotalTruePositives?: number | null;
  amlTotalFalsePositives?: number | null;
  amlTotalUnknown?: number | null;
  amlTotalUnresolved?: number | null;
  amlUnresolvedPep?: number | null;
  amlUnresolvedSanction?: number | null;
  amlUnresolvedWatchlist?: number | null;
  amlUnresolvedAdverseMedia?: number | null;
  reportType?: string | null;
  reportDocumentId?: string | null;
  reportAttachmentId?: string | null;
  issueCount: number;
  issues?: string | null;
  workflowStartedAt?: string | null;
  workflowEndedAt?: string | null;
  workflowUpdatedAt?: string | null;
}

/** Curated per-run KYB stakeholder row persisted locally. */
export interface ApplicationKybStakeholderRow {
  frankieOneEntityId: string;
  displayName?: string | null;
  givenName?: string | null;
  middleName?: string | null;
  familyName?: string | null;
  dateOfBirth?: string | null;
  isOfficial: boolean;
  isOwner: boolean;
  isDirector: boolean;
  isSecretary: boolean;
  directorAppointmentDate?: string | null;
  directorStatus?: string | null;
  isShareholder: boolean;
  isUltimateBeneficialOwner: boolean;
  totalShares?: number | null;
  beneficialOwnershipPct?: number | null;
  nonBeneficialOwnershipPct?: number | null;
  totalOwnershipPct?: number | null;
}

/**
 * Curated per-run KYB address row persisted locally. These are registry-sourced observations
 * with a validity window, kept separate from the mutable applicant-entered `ApplicationAddress`.
 */
/**
 * Curated KYB address row. Slot FKs on ApplicationKYBResult / ApplicationKYBStakeholder
 * imply the address role; this row has no addressType of its own.
 */
export interface ApplicationKybAddressRow {
  frankieOneAddressId?: string | null;
  unitNumber?: string | null;
  streetNumber?: string | null;
  streetName?: string | null;
  streetType?: string | null;
  suburb?: string | null;
  state?: string | null;
  postcode?: string | null;
  country?: string | null;
  unformattedAddress?: string | null;
  validFrom?: string | null;
  validTo?: string | null;
  /** Registry that asserted the address, e.g. ASIC or ABR. */
  source?: string | null;
}

/** One ApplicationKYBRun as shown on the admin KYB screen. */
export interface ApplicationKybRunHistoryItem {
  id: number;
  status: KybResultStatus;
  frankieOneEntityId: string | null;
  requestId: string | null;
  workflowExecutionId: string | null;
  workflowName: string;
  serviceName: string;
  entityCustomerReference: string;
  initiationAttemptCount: number;
  resultProcessingAttemptCount: number;
  /** Null when the run was started automatically; the UI renders "System". */
  triggeredByName: string | null;
  createdAt: string;
  initiatedAt: string | null;
  callbackReceivedAt: string | null;
  completedAt: string | null;
  lastUpdate: string;
  lastError: string | null;
  lastErrorAt: string | null;
}

export type KybReevaluationStatus = 'IN_PROGRESS' | 'RETRY_PENDING' | 'COMPLETED' | 'FAILED';

export type KybQueueView = 'ACTION_REQUIRED' | 'MONITORING' | 'ALL';

export type KybApplicationStateFilter = 'SUBMITTED' | 'DRAFT' | 'ALL';

export type KybAttentionReason =
  | 'RECONCILIATION_REQUIRED'
  | 'RUN_FAILED'
  | 'RUN_STALLED'
  | 'REEVALUATION_FAILED'
  | 'REEVALUATION_STALLED';

export interface ApplicationKybLatestReevaluationSummary {
  id: number;
  status: KybReevaluationStatus;
  requestedAt: string;
  respondedAt: string | null;
  lastError: string | null;
}

export interface ApplicationKybQueueSummary {
  actionRequired: number;
  reconciliationRequired: number;
  failed: number;
  stalled: number;
  monitoring: number;
  all: number;
}

/** Why a re-evaluation was sent. Diagnostic only; new triggers may add values. */
export type KybReevaluationTriggerReason =
  | 'submit'
  | 'kyb-complete'
  | 'anzsic-edit'
  | 'entity-risk-changed'
  | 'manual-retry';

/**
 * One FrankieOne risk re-evaluation request and its response.
 *
 * Overlapping requests against the same run are expected — every trigger sends a fresh evaluate —
 * so these are listed rather than collapsed to a latest state. `respondedAt` and `durationMs` stay
 * null until the WorkflowComplete callback for that execution is processed, which is what makes a
 * stalled re-evaluation visible.
 */
export interface ApplicationKybReevaluationItem {
  id: number;
  applicationKybRunId: number;
  triggerReason: KybReevaluationTriggerReason | string;
  /** True when no custom attribute changed, so only evaluate was sent. */
  evaluateOnly: boolean;
  anzsicCode: string | null;
  status: KybReevaluationStatus;
  requestId: string | null;
  workflowExecutionId: string | null;
  attemptCount: number;
  requestedAt: string;
  respondedAt: string | null;
  durationMs: number | null;
  riskLevel: string | null;
  riskScore: number | null;
  lastError: string | null;
  /** Null when triggered automatically; the UI renders "System". */
  triggeredByName: string | null;
}

/**
 * One row of the admin KYB list: a commercial entity and its most recent KYB run.
 *
 * The grain is (application, commercial entity) rather than the run, so every row reflects
 * current state and Retry always targets the latest run. Entities that never produced a run do
 * not appear at all — the list is driven from ApplicationKYBRun.
 */
export interface ApplicationKybEntityRun {
  applicationId: number;
  applicationCommercialEntityId: number;
  legalName: string | null;
  abn: string | null;
  acn: string | null;
  entityType: string | null;
  isPrimary: boolean;
  isBorrower: boolean;
  isGuarantor: boolean;
  reconciliationStatus: KybReconciliationStatus | null;
  attentionReasons: KybAttentionReason[];
  primaryAttentionReason: KybAttentionReason | null;
  attentionSince: string | null;
  latestReevaluation: ApplicationKybLatestReevaluationSummary | null;
  latestRun: ApplicationKybRunHistoryItem;
  /** Superseded runs for the same entity, newest first. Empty in the common case. */
  previousRuns: ApplicationKybRunHistoryItem[];
  /**
   * Risk re-evaluations across all of the entity's runs, newest first. Empty until the entity has a
   * completed KYB and a submitted application.
   */
  reevaluations: ApplicationKybReevaluationItem[];
}

export type ApplicationKybRunSortColumn =
  | 'attentionPriority'
  | 'attentionSince'
  | 'createdAt'
  | 'lastUpdate'
  | 'status'
  | 'applicationId';

export interface ApplicationKybRunFilters {
  queueView?: KybQueueView;
  /** Submitted includes every application status except Draft; absent preserves the API's legacy all-state behaviour. */
  applicationState?: KybApplicationStateFilter;
  attentionReasons?: KybAttentionReason[];
  /** Empty or absent means every status. Sent to the API as a comma-separated list. */
  statuses?: KybResultStatus[];
  reconciliationStatuses?: KybReconciliationStatus[];
  /** Matches applicationId when numeric, otherwise ABN / ACN / any run's frankieOneEntityId. */
  search?: string;
  /**
   * Inclusive bounds on the run's createdAt, as ISO 8601 UTC instants (not calendar dates).
   * Callers pick a local day and send the instants bracketing it, because createdAt is UTC.
   */
  startDate?: string;
  endDate?: string;
}

export interface ApplicationKybQueueResponse
  extends PaginablePayloadApiResponse<ApplicationKybEntityRun> {
  summary: ApplicationKybQueueSummary;
}

/**
 * Outcome of a manual retry, so the toast can say which path was taken: the result was pulled from
 * FrankieOne, or nothing was available and a new workflow was started.
 */
export interface RetryApplicationKybRunResult {
  applicationKybRunId: number;
  action: 'initiated' | 'result-reprocessed';
}

/**
 * Outcome of a batch retry. Runs are retried independently, so a partial result is normal and both
 * halves are reported rather than failing the whole request on the first bad run.
 */
export interface RetryApplicationKybRunsResult {
  succeeded: RetryApplicationKybRunResult[];
  failed: { applicationKybRunId: number; message: string }[];
}

/** Upper bound on one batch retry; each run may make its own calls out to FrankieOne. */
export const MAX_KYB_RUN_RETRY_BATCH = 25;